import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const bounds = new window.mapboxgl.LngLat(latlng.lng, latlng.lat).toBounds(distance);
  return new SDKLatLngBounds(
    new SDKLatLng(bounds.getNorth(), bounds.getEast()),
    new SDKLatLng(bounds.getSouth(), bounds.getWest())
  );
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  waitForLibraries() {
    return new Promise(resolve => {
      const ready = () => (
        typeof window !== 'undefined' &&
        window.mapboxgl &&
        window.mapboxgl.accessToken &&
        window.mapboxSdk
      );
      if (ready()) return resolve();
      const maybeResolve = () => { if (ready()) resolve(); };
      window.addEventListener('mapboxgl:ready', maybeResolve, { once: true });
      window.addEventListener('mapboxsdk:ready', maybeResolve, { once: true });
    });
  }

  getClient() {
    const libLoaded =
      typeof window !== 'undefined' &&
      window.mapboxgl &&
      window.mapboxgl.accessToken &&
      window.mapboxSdk;
    if (!libLoaded) {
      // Defer until both GL, SDK, and accessToken are ready
      return this.waitForLibraries().then(() => this.getClient());
    }
    if (!this._client) {
      this._client = window.mapboxSdk({
        accessToken: window.mapboxgl.accessToken,
      });
    }
    return Promise.resolve(this._client);
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, countryLimit, locale, proximity) {
    const limitCountriesMaybe = countryLimit ? { countries: countryLimit } : {};
    const proximityMaybe = proximity && proximity.length === 2
      ? { proximity }
      : {} // { proximity: [-98.5795, 39.8283] }; //default to US

    return this.getClient()
      .then(client => client.geocoding.forwardGeocode({
        query: search,
        limit: 5,
        ...limitCountriesMaybe,
        language: [locale],
        ...proximityMaybe,
        types: ['address', 'poi'],
      }).send())
      .then(response => ({
        search,
        predictions: response.body.features,
      }));
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance = GENERATED_BOUNDS_DEFAULT_DISTANCE) {
    // Handle current location case
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        // Use Mapbox's reverse geocoding to get address details
        return this.getClient()
          .then(client => client.geocoding.reverseGeocode({
            query: [latlng.lng, latlng.lat],
            limit: 1,
            types: ['address', 'place', 'region', 'postcode', 'country'],
          }).send())
          .then(response => {
            const features = response.body.features;
            if (!features || features.length === 0) {
              return {
                address: 'Current Location',
                origin: latlng,
                bounds: locationBounds(latlng, currentLocationBoundsDistance),
              };
            }

            const feature = features[0];
            const context = feature.context || [];
            const address = feature.place_name || 'Current Location';

            // Extract address components from context
            const addressComponents = context.reduce((acc, item) => {
              const idParts = item.id.split('.');
              const type = idParts[0];
              return { ...acc, [type]: item.text };
            }, {});

            return {
              address: address,
              origin: latlng,
              bounds: locationBounds(latlng, currentLocationBoundsDistance),
              city: addressComponents.place || addressComponents.locality || '',
              state: addressComponents.region || '',
              country: addressComponents.country || '',
              postcode: addressComponents.postcode || '',
              region: addressComponents.region || ''
            };
          })
          .catch(error => {
            console.error('Reverse geocoding failed:', error);
            // Fallback to basic location info if reverse geocoding fails
            return {
              address: 'Current Location',
              origin: latlng,
              bounds: locationBounds(latlng, currentLocationBoundsDistance),
            };
          });
      });
    }

    const cityContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('place') || context.id.startsWith('district'));

    // Some regions don't have a 'place'/'district' context feature but do have 'locality'
    // (mirrors the fallback already used in the current-location reverse-geocode branch above).
    const localityContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('locality'));

    const regionContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('region'));

    const countryContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('country'));

    const postcodeContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('postcode'));

    const stateContext = prediction
      && prediction.context
      && prediction.context.length
      && prediction.context.filter(context => context.id.startsWith('region'));

    const cityText = cityContext && cityContext.length
      ? cityContext[0].text
      : (localityContext && localityContext.length ? localityContext[0].text : "");
    const regionText = regionContext && regionContext.length ? regionContext[0].text : "";
    const countryText = countryContext && countryContext.length ? countryContext[0].text : "";
    const postcodeText = postcodeContext && postcodeContext.length ? postcodeContext[0].text : "";
    const stateText = stateContext && stateContext.length ? stateContext[0].text : "";

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
      city: cityText,
      country: countryText,
      region: regionText,
      postcode: postcodeText,
      state: stateText
    });
  }
}

export default GeocoderMapbox;
